import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered,
  Heading2, Heading3, Quote, Link as LinkIcon, Image as ImageIcon, Undo2, Redo2, Code,
} from 'lucide-react';
import { AdminAPI } from '../../lib/api';

const Btn = ({ active, onClick, title, children, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded text-sm border ${active ? 'bg-[#1F4D3F] text-white border-[#1F4D3F]' : 'bg-white text-[#2D2A26] border-transparent hover:bg-[#F5F1EA]'} disabled:opacity-40`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value = '', onChange, placeholder = 'Tapez votre contenu…', testId, minHeight = 200 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-[#1F4D3F] underline' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full h-auto my-2' } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-content prose max-w-none focus:outline-none px-3 py-2',
        style: `min-height: ${minHeight}px`,
        'data-testid': testId || 'rich-text-editor',
      },
    },
  });

  // Sync external value changes (e.g., on dialog open)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current && !editor.isFocused) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('URL du lien (laisser vide pour supprimer) :', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  }, [editor]);

  const addImage = useCallback(async () => {
    if (!editor) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const r = await AdminAPI.upload(file);
        editor.chain().focus().setImage({ src: r.url || r.path }).run();
      } catch {
        const url = window.prompt('Upload échoué. URL externe ?');
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }
    };
    input.click();
  }, [editor]);

  if (!editor) return <div className="border border-[#E8E0D0] rounded-lg p-3 text-sm text-[#7A6F5C]">Chargement de l'éditeur…</div>;

  return (
    <div className="border border-[#E8E0D0] rounded-lg overflow-hidden bg-white" data-testid={testId ? `${testId}-wrapper` : 'rich-text-wrapper'}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-[#FAF7F2] border-b border-[#E8E0D0]">
        <Btn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras"><Bold className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique"><Italic className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Souligné"><UnderlineIcon className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barré"><Strikethrough className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E8E0D0] mx-1" />
        <Btn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre H2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Titre H3"><Heading3 className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E8E0D0] mx-1" />
        <Btn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces"><List className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation"><Quote className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code"><Code className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E8E0D0] mx-1" />
        <Btn active={editor.isActive('link')} onClick={setLink} title="Insérer un lien"><LinkIcon className="w-4 h-4" /></Btn>
        <Btn onClick={addImage} title="Insérer une image"><ImageIcon className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E8E0D0] mx-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler"><Undo2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir"><Redo2 className="w-4 h-4" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
